import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import {
    FormControl,
    FormGroup,
    Validators,
} from '@angular/forms';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { IExecutionSetting } from '@models';
import { TranslateService } from '@ngx-translate/core';
import { ISuggestValue } from '@uipath/angular/components/ui-suggest';
import UiValidators from '@validators';

enum ValueTypes {
    multipleChoice = 'MultipleChoice',
    integer = 'Integer',
    boolean = 'Boolean',
}

class ExecutionSettingsFormGroup extends FormGroup {
    public previouslyEnabledKeys: string[] = [];
    private _value: any;

    public disable(opts: { onlySelf?: boolean, emitEvent?: boolean } = {}) {
        this.previouslyEnabledKeys = Object.keys(this.controls)
            .filter((key) => this.get([key])!.enabled);

        super.disable(opts);
    }

    public enable() {
        this.previouslyEnabledKeys
            .forEach(
                key => this.get(key)!.enable(),
            );
        this.previouslyEnabledKeys = [];
    }

    public set value(value) {
        this._value = value;
    }

    public get value() {
        if (this.disabled) {
            return this._getExecutionSettingsValue();
        }
        return this._cleanOutput(this._value);
    }

    private _getExecutionSettingsValue() {
        if (!this.previouslyEnabledKeys.length) {
            return {};
        }

        const output = this._cleanOutput(this.getRawValue());
        return this.previouslyEnabledKeys
            .reduce((acc, curr) => {
                acc[curr] = output[curr];
                return acc;
            }, {} as Record<string, any>);
    }

    private _cleanOutput = (values: Record<string, string | ISuggestValue[]>) => {
        return Object.keys(values).reduce(
            (prev, curr) => {
                const currentValue = values[curr];

                if (Array.isArray(currentValue)) {
                    const [value] = currentValue as ISuggestValue[];
                    prev[curr] = value.id;
                } else {
                    prev[curr] = currentValue;
                }

                return prev;
            }, {} as Record<string, string | number>);
    }
}

@Component({
    selector: 'ui-execution-settings',
    templateUrl: 'ui-execution-settings.component.html',
    styleUrls: ['ui-execution-settings.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UiExecutionSettingsComponent implements OnInit {
    @Input()
    public settingsConfig: IExecutionSetting[] = [];

    @Input()
    public settingsValues: Record<string, string | number> = {};

    @Input()
    public disabled = false;

    public readonly maxIntegerValue = Math.pow(2, 15) - 1;

    public formGroup: ExecutionSettingsFormGroup;
    public dropdownEntries: { [key: string]: ISuggestValue[] } = {};

    public ValueTypes = ValueTypes;

    constructor(
        private _translateService: TranslateService,
    ) {
        this.formGroup = new ExecutionSettingsFormGroup({});
    }

    ngOnInit() {
        this.settingsConfig.forEach(setting => {
            const validators = [
                Validators.required,
                ...(
                    setting.valueType === ValueTypes.integer ?
                        [UiValidators.range(0, this.maxIntegerValue)] :
                        []
                ),
            ];

            if (setting.valueType === ValueTypes.multipleChoice) {
                this.dropdownEntries[setting.key] = setting.possibleValues.map(
                    possibleValue => this._valueToDropdownEntry(setting.key, possibleValue),
                );
            }

            this.formGroup.addControl(
                setting.key,
                new FormControl({
                    value: this._getInitialValue(setting),
                    disabled: this.settingsValues[setting.key] == null,
                }, validators),
            );
        });

        if (this.disabled) {
            this.formGroup.disable();
        }
    }

    public onSettingToggle(event: MatSlideToggleChange, settingKey: string) {
        event.checked ?
            this.formGroup.get(settingKey)!.enable() :
            this.formGroup.get(settingKey)!.disable();
    }

    public resetSettings() {
        for (const setting of this.settingsConfig) {
            const field = this.formGroup.get(setting.key)!;

            field.setValue(this._getInitialValue(setting));
            field.disable();
        }
    }

    protected _getInitialValue(setting: IExecutionSetting): ISuggestValue | string {
        const defaultValue = this.settingsValues[setting.key] != null ?
            this.settingsValues[setting.key] :
            setting.defaultValue;

        return setting.valueType === ValueTypes.multipleChoice ?
            [this._valueToDropdownEntry(setting.key, defaultValue)] :
            defaultValue;
    }

    protected _valueToDropdownEntry(settingKey: string, value: string): ISuggestValue {
        return {
            id: value,
            text: this._translateService.instant(`ExecutionSettings_${settingKey}_${value}`),
        };
    }
}
